<template>
    <div v-allowed="'view_expenses'">

        <div v-if="loadingExpenses" style="margin-top:10px" class="text-center">
            <v-skeleton-loader type="table"></v-skeleton-loader>
        </div>

        <!-- Empty state - no financial data -->
        <div v-else-if="!loadingExpenses && expenses.length === 0"
            class="d-flex align-center justify-center" style="min-height: 70vh;">
            <v-row class="d-flex align-center justify-center">
                <v-col class="d-flex flex-column align-center justify-center" cols="12">
                    <v-img class="mb-6" height="130" :src="require('@/assets/icons/admin+/financial_money.svg')"
                        contain></v-img>
                    <div class="text-center mb-6">
                        <span class="font-roboto text-empty">
                            {{ $t("message.transactions_admin_plus.no_expenses_yet") }}
                        </span>
                        <div class="text-body-1 text-empty">
                            {{ $t("message.transactions_admin_plus.add_your_first_expense") }}
                        </div>
                    </div>
                    <PlusButton small intent="primary"
                        @click="navigateTo({ name: 'manageexpense-adminplus', params: { transactionId: 'new' } })">
                        <span class="font-roboto text-button d-inline">add expense</span>
                    </PlusButton>
                </v-col>
            </v-row>
        </div>

        <div v-else>
            <v-card :elevation="isSmallerDisplay() ? 1 : 0"
                v-if="expensesGraphs.selectedRange && expensesGraphs.selectedRange.ready"
                class="ma-1 mb-8 mt-3 ma-lg-0 pa-6 pa-lg-2 rounded-lg">
                <v-row class="mb-1">
                    <v-col cols="12">
                        <v-row class="d-flex align-center">
                            <v-col cols="12" md="8" class="d-flex align-center">
                                <div class="font-roboto font-weight-medium" style="font-size: 1.25rem;">
                                    {{ $t("message.transactions_admin_plus.expenses_by_category") }} {{ selectedRangeTitle
                                    }}
                                </div>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex justify-start justify-md-end">
                                <v-select dense outlined hide-details :items="expensesGraphRangeFilters"
                                    :value="expensesGraphRange" @input="$emit('update:expenses-graph-range', $event)"
                                    label="Range" class="expenses-range-select" />
                            </v-col>
                        </v-row>

                        <v-row class="mt-4 align-center">
                            <v-col cols="12" md="3">
                                <div class="expenses-donut-wrap">
                                    <ApexChart type="donut" :options="styledExpensesDonutOptions" height="350"
                                        :series="expensesGraphs.selectedRange.data"></ApexChart>
                                    <div class="expenses-donut-total">
                                        <div class="expenses-donut-total-amount">
                                            {{ currencySymbol }}{{ totalExpensesSelectedRange.toFixed(2) }}
                                        </div>
                                        <div class="expenses-donut-total-label">Total expenses</div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" md="4">
                                <div v-for="(item, idx) in expenseBreakdownItems" :key="`expense-breakdown-${idx}`"
                                    class="mb-5">
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-subtitle-1">{{ item.label }}</span>
                                        <span class="text-subtitle-1 font-weight-medium">
                                            {{ currencySymbol }}{{ item.value.toFixed(2) }}
                                        </span>
                                    </div>
                                    <v-progress-linear :value="item.percentage" :color="item.color"
                                        background-color="#D5D9DD" rounded height="6"></v-progress-linear>
                                </div>
                            </v-col>
                        </v-row>

                    </v-col>
                </v-row>

            </v-card>
            <v-row v-else class="mb-1">
                <v-col cols="12">
                    <div class="text-subtitle-2 text-center">{{ selectedRangeTitle }}</div>
                    <v-skeleton-loader type="image" height="350"></v-skeleton-loader>
                </v-col>
            </v-row>

            <v-card elevation="0" outlined class="mb-lg-3 mt-1 pa-6 rounded-lg">
                <v-card-text class="pa-0 mb-1">
                    <v-row>
                        <v-col cols="12" lg="6">
                            <v-text-field clearable outlined dense hide-details prepend-inner-icon="mdi-magnify"
                                :label="'Search by expense name'" v-model="expenseNameFilter" />
                        </v-col>
                        <v-col cols="12" lg="2" :class="displayFilters ? '' : 'd-lg-flex d-none'">
                            <v-select clearable outlined dense :value="expensesTagFilter"
                                @input="$emit('update:expenses-tag-filter', $event)" :items="expenseTags" hide-details
                                :label="$t('message.transactions_admin_plus.category')"></v-select>
                        </v-col>

                        <v-col cols="12" lg="2" :class="displayFilters ? '' : 'd-lg-flex d-none'">
                            <DatePicker :show-time="false" :value="fromFilter"
                                @input="$emit('update:from-filter', $event)" :past-event="true">
                            </DatePicker>
                        </v-col>

                        <v-col cols="12" lg="2" :class="displayFilters ? '' : 'd-lg-flex d-none'">
                            <DatePicker :show-time="false" :value="toFilter" @input="$emit('update:to-filter', $event)"
                                :past-event="true">
                            </DatePicker>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pa-0 d-lg-none d-flex justify-center">
                            <v-card-actions class="pr-0 mr-0">
                                <v-btn @click="displayFilters = !displayFilters" text plain
                                    class="pa-0 ma-0 text-lowercase">
                                    <span
                                        class="font-roboto text-body-2 text-decoration-underline text-primary text-uppercase">
                                        {{
                                            !displayFilters ? $t('message.transactions_admin_plus.show_more_filters') :
                                                $t('message.transactions_admin_plus.members_hide_filters') }}
                                    </span>
                                </v-btn>
                            </v-card-actions>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-row class="d-flex align-center mb-8 mt-8">
                <v-col cols="12" md="6" class="d-flex align-center flex-wrap" style="gap:12px">
                    <div class="font-roboto font-weight-medium" style="font-size: 1.25rem;">
                        {{ $t('message.transactions_admin_plus.expenses') }} ({{ expensesFilteredForTable.length }})
                    </div>
                    <v-chip small outlined color="primary" class="font-roboto font-weight-medium">
                        Total: {{ currencySymbol }}{{ filteredTotal.toFixed(2) }}
                    </v-chip>
                </v-col>
                <v-col cols="12" md="6" class="font-roboto d-flex justify-start justify-md-end">
                    <v-btn outlined elevation="0" small @click="$emit('add-new-expense')" color="primary">{{
                        $t('message.transactions_admin_plus.add_expense')
                        }}</v-btn>
                </v-col>
            </v-row>



            <v-skeleton-loader v-if="loadingExpenses" type="table"></v-skeleton-loader>
            <div v-else>
                <v-data-table item-key="_id" :headers="isSmallerDisplay() ? expensesHeaderMobile : expensesHeaders"
                    :items="expensesFilteredForTable" :loading="loadingExpenses" class="tabble-expenses"
                    :no-data-text="$t('message.transactions_admin_plus.no_data')"
                    :footer-props="{ itemsPerPageText: $t('message.transactions_admin_plus.rows_per_page'), itemsPerPageOptions: [25, 50, 75] }"
                    @click:row="$emit('go-to-expense', $event)" :hide-default-header="isSmallerDisplay()">

                    <!-- mobile -->

                    <template slot="item.expenseNameMobile" slot-scope="{ item }">
                        <div class="d-flex flex-column pt-4 pb-4">
                            <span class="font-roboto text-body-1 font-weight-medium plusGreyDarken3--text">{{
                                item.expenseName }}</span>
                            <span class="font-roboto text-body-2 font-weight-regular plusGreyDarken2--text">{{
                                item.category }}</span>

                            <span class="font-roboto text-body-caption font-weight-regular plusGreyDarken2--text">{{
                                `Added on ${$d(new Date(item.expenseDate), 'month_day_year', $i18n.locale)}` }}</span>
                        </div>
                    </template>

                    <template slot="item.amountMobile" slot-scope="{ item }">
                        <span class="font-roboto text-body-1 font-weight-medium plusGreyDarken3--text">{{ currencySymbol
                            }}{{
                                item.amount
                            }}</span>
                    </template>


                    <!-- desktop -->
                    <template slot="item.expenseName" slot-scope="{ item }">
                        <span class="font-roboto text-subtitle-2 font-weight-regular">{{ item.expenseName }}</span>
                    </template>

                    <template slot="item.category" slot-scope="{ item }">
                        <span class="font-roboto text-subtitle-2 font-weight-regular">{{ item.category }}</span>
                    </template>

                    <template slot="item.amount" slot-scope="{ item }">
                        <span class="font-roboto text-subtitle-2 font-weight-regular">{{ currencySymbol }}{{
                            item.amount
                            }}</span>
                    </template>

                    <template slot="item.taxAmount" slot-scope="{ item }">
                        <span class="font-roboto text-subtitle-2 font-weight-regular">{{ currencySymbol }}{{
                            item.taxAmount }}</span>
                    </template>

                    <template slot="item.expenseDate" slot-scope="{ item }">
                        <span v-if="item.expenseDate" class="font-roboto text-subtitle-2 font-weight-regular">
                            {{ $d(new Date(item.expenseDate), 'month_day_year', $i18n.locale) }}
                        </span>
                    </template>

                    <template slot="item.hasReceipt" slot-scope="{ item }">
                        <span class="font-roboto text-subtitle-2 font-weight-regular">
                            {{ item.receipt ? 'Yes' : 'No' }}
                        </span>

                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import PlusButton from "@/components/plus/PlusButton.vue";
import ApexChart from 'vue-apexcharts';

export default {
    name: "ViewExpenses",
    components: {
        DatePicker,
        ApexChart,
        PlusButton
    },
    props: {
        academy: { type: Object, default: null },
        expensesGraphs: {
            type: Object,
            default: () => ({ ready: false, thisMonth: {}, thisYear: {}, colors: [], selectedRange: { ready: false, options: {}, data: [] } })
        },
        expensesTagFilter: { default: null },
        expenseTags: { type: Array, default: () => [] },
        fromFilter: { default: null },
        toFilter: { default: null },
        loadingExpenses: { type: Boolean, default: false },
        expenses: { type: Array, default: () => [] },
        expensesFiltered: { type: Array, default: () => [] },
        expensesGraphRange: { type: String, default: 'thismonth' },
        currencySymbol: { type: String, default: '$' },
        adminFilter: { default: null },
        admins: { type: Array, default: () => [] }
    },
    data() {
        return {
            expenseNameFilter: '',
            expensesGraphRangeFilters: [
                { text: 'This month', value: 'thismonth' },
                { text: 'Last month', value: 'month' },
                { text: 'Last 3 months', value: '3' },
                { text: 'Last 6 months', value: '6' },
                { text: 'Last 12 months', value: '12' }
            ],
            expensesHeaders: [
                { text: 'Expense name', value: 'expenseName', sortable: true },
                { text: 'Category', value: 'category', sortable: true },
                { text: 'Amount', value: 'amount', sortable: true },
                { text: 'Tax amount', value: 'taxAmount', sortable: true },
                { text: 'Date', value: 'expenseDate', sortable: true },
                { text: 'Uploaded receipt?', value: 'hasReceipt', sortable: false }
            ],

            expensesHeaderMobile: [
                { text: 'Expense name', value: 'expenseNameMobile', sortable: true },
                { text: 'Amount', value: 'amountMobile', sortable: true, align: 'end' },
            ],
            displayFilters: false
        };

    },
    computed: {
        styledExpensesDonutOptions() {
            const options = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.options || {})
                : {};
            const totalExpenses = this.totalExpensesSelectedRange;
            const palette = this.chartColors;

            return {
                ...options,
                chart: {
                    ...(options.chart || {}),
                    type: 'donut'
                },
                legend: { show: false },
                stroke: {
                    show: true,
                    width: 4,
                    colors: ['#FFFFFF']
                },
                colors: palette,
                fill: {
                    ...(options.fill || {}),
                    colors: palette
                },
                dataLabels: { enabled: false },
                plotOptions: {
                    ...(options.plotOptions || {}),
                    pie: {
                        ...((options.plotOptions && options.plotOptions.pie) || {}),
                        donut: {
                            ...(((options.plotOptions && options.plotOptions.pie && options.plotOptions.pie.donut) || {})),
                            size: '86%',
                            labels: {
                                show: true,
                                name: { show: false },
                                value: { show: false },
                                total: {
                                    show: false,
                                    showAlways: true,
                                    label: 'Total expenses',
                                    fontSize: '18px',
                                    fontWeight: 400,
                                    color: '#616161',
                                    formatter: () => `${this.currencySymbol}${totalExpenses.toFixed(2)}`
                                }
                            }
                        }
                    }
                }
            };
        },
        chartLabels() {
            const options = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.options || {})
                : {};
            return Array.isArray(options.labels) ? options.labels : [];
        },
        chartColors() {
            const options = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.options || {})
                : {};
            if (Array.isArray(options.colors) && options.colors.length) return options.colors;
            if (options.fill && Array.isArray(options.fill.colors) && options.fill.colors.length) return options.fill.colors;
            return [];
        },
        totalExpensesSelectedRange() {
            const series = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.data || [])
                : [];
            return series.reduce((/** @type {number} */ sum, /** @type {number | string} */ val) => sum + (parseFloat(String(val)) || 0), 0);
        },
        expenseBreakdownItems() {
            const series = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.data || [])
                : [];
            const labels = this.chartLabels;
            const colors = this.chartColors;
            const maxValue = series.reduce((/** @type {number} */ max, /** @type {number | string} */ val) => Math.max(max, parseFloat(String(val)) || 0), 0);

            return series.map((/** @type {number | string} */ rawValue, /** @type {number} */ idx) => {
                const value = parseFloat(String(rawValue)) || 0;
                return {
                    label: labels[idx] || `Category ${idx + 1}`,
                    value,
                    color: colors[idx] || '#6200EE',
                    percentage: maxValue > 0 ? (value / maxValue) * 100 : 0
                };
            });
        },
        selectedRangeTitle() {
            switch (this.expensesGraphRange) {
                case 'thismonth':
                    return 'This month';
                case 'month':
                    return 'Last month';
                case '3':
                    return 'Last 3 months';
                case '6':
                    return 'Last 6 months';
                case '12':
                    return 'Last 12 months';
                default:
                    return '';
            }
        },
        expensesFilteredByName() {
            const list = Array.isArray(this.expensesFiltered) ? this.expensesFiltered : [];
            const q = (this.expenseNameFilter || '').trim().toLowerCase();
            if (!q) return list;

            return list.filter((e) => {
                const name = (e && e.expenseName) || ((e && e.items && e.items[0] && e.items[0].name) ? e.items[0].name : '');
                return String(name).toLowerCase().includes(q);
            });
        },
        safeChartSeries() {
            const data = this.expensesGraphs && this.expensesGraphs.selectedRange
                ? (this.expensesGraphs.selectedRange.data || [])
                : [];
            return data.map(v => (v != null && !isNaN(Number(v)) ? Number(v) : 0));
        },
        filteredTotal() {
            return this.expensesFilteredForTable.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
        },
        expensesFilteredForTable() {
            const expenses = this.expensesFilteredByName.map((e) => {
                const rawAmount = typeof e.amount === 'number' ? e.amount : parseFloat(e.amount || 0);
                const amountValue = Math.abs(rawAmount);

                const taxValueRaw = (e && (e.tax !== undefined ? e.tax : (e.taxAmount !== undefined ? e.taxAmount : 0)));
                const taxValue = parseFloat(taxValueRaw || 0);

                const dateRaw = e.completed ? e.completed : e.time;
                const dateValue = dateRaw ? new Date(dateRaw).toISOString() : null;

                const categoryValue = Array.isArray(e.tags) ? e.tags.join(', ') : '';
                return {
                    ...e,
                    expenseName: (e && e.items && e.items[0] && e.items[0].name) ? e.items[0].name : '',
                    category: categoryValue,
                    amount: amountValue.toFixed(2),
                    taxAmount: isNaN(taxValue) ? '0.00' : taxValue.toFixed(2),
                    expenseDate: dateValue,
                    hasReceipt: !!e.receipt
                };
            });
            return expenses;
        }
    },
    emits: [
        'add-new-expense',
        'get-expenses',
        'go-to-expense',
        'update:expenses-graph-range',
        'update:expenses-tag-filter',
        'update:from-filter',
        'update:to-filter',
        'update:admin-filter'
    ]
};
</script>

<style>
.tabble-expenses .v-data-table-header th,
.tabble-expenses .v-data-table td {
    font-family: "Roboto", sans-serif !important;
    font-weight: bold !important;
    font-size: 12px !important;
}

.tabble-expenses .v-data-table__mobile-row__cell {
    text-align: left !important;
}

.apexcharts-datalabel-label {
    font-family: "Roboto", sans-serif !important;
}

.apexcharts-datalabel-value {
    font-family: "Roboto", sans-serif !important;
}

.expenses-donut-wrap {
    position: relative;
}

.expenses-donut-total {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    text-align: center;
}

.expenses-donut-total-amount {
    font-family: "Roboto", sans-serif;
    font-size: 2.1rem;
    line-height: 1.1;
    font-weight: 400;
}

.expenses-donut-total-label {
    margin-top: 4px;
    font-family: "Roboto", sans-serif;
    font-size: 1.33rem;
    line-height: 1.15;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.60);
}

.expenses-range-select {
    max-width: 220px;
    width: 100%;
}

@media (max-width: 1264px) {
    .expenses-donut-total-amount {
        font-size: 2rem;
    }

    .expenses-donut-total-label {
        font-size: 1.6rem;
    }

    .tabble-expenses {
        border: 1px solid #EEE;
        padding: 1rem !important;
        border-radius: 4px;
    }
}
</style>